import Indicator from "./Indicator";
import {useRouter} from "next/router";
import {useQuery} from '@tanstack/react-query';
import {useEffect, useState} from "react";
import styled from "styled-components";
import {useMessages} from "../../hooks/useMessages";
import {request} from "../../shared/httpInterceptor";
import {IAvailableCountry} from "../../interfaces/IAvailableCountry";
import Select from "react-select";
import Button from "../Button";
import {IAvailableLanguage} from "../../interfaces/IAvailableLanguage";
import {useCountryLang} from "../../hooks/useCountryLang";
import Icon from "../Icon";
import {ICountriesAndLanguages} from "../../interfaces/ICountriesAndLanguages";
import {getTheme} from "../../shared/themeUtils";
import Cookies from "js-cookie";

const Contain = styled.div`
    .subheading {
        display: inline-block;
        position: relative;
        text-align: center;
        font-weight: bold;
        margin-right: auto;
        margin-bottom: ${getTheme("space.2")};
        font-size: ${getTheme("fontSizes.large")};
    }

    .subheading::before {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        height: 10px; /* Height of the marker effect */
        background-color: ${getTheme("colors.accentLight")};
        z-index: -1; /* Behind the text */
        border-radius: 4px;
        padding: 0;
    }

    .divider {
        border-top: 1px solid ${getTheme("colors.border")};
        margin: ${getTheme("space.2")} 0;
    }

    .countries {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-around;

        .entry {
            padding: ${getTheme("space.1")} 0;
            width: 33%;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            margin-bottom: ${getTheme("space.0")};
            cursor: pointer;

            .flag {
                img {
                    max-width: 40px;
                    cursor: pointer;
                    border: 1px solid ${getTheme("colors.border")};
                }
            }

            .text {
                font-size: ${getTheme("fontSizes.small")};
                margin-top: ${getTheme("space.0")};
                text-align: center;
            }
        }

        .active.entry {
            border-radius: ${getTheme("space.0")};
            position: relative;

            &::after {
                content: "";
                background-image: url("/icons/tick-gradient.svg");
                background-position: center;
                background-size: contain;
                width: 30px;
                height: 30px;
                position: absolute;
                top: 0;
                right: 16px;
            }
        }
    }

    .languages {
        .quick-select {
            margin-top: ${getTheme("space.1")};
            display: flex;
            justify-content: space-between;
            margin-bottom: ${getTheme("space.2")};

            .active-lang {
                background: ${getTheme("colors.accent")};
                color: ${getTheme("colors.white")};
            }
        }
    }

    .save-selection {
        position: sticky;
        bottom: 0;
    }
`

const Header = styled.div`
    margin-top: ${getTheme("space.1")};
    margin-bottom: ${getTheme("space.2")};
    padding-bottom: ${getTheme("space.2")};
    border-bottom: 1px solid ${getTheme("colors.border")};
    display: flex;
    align-items: center;
`

const BtnLabel = styled.div`
    display: flex;

    .label-text {
        font-size: ${getTheme("fontSizes.small")};
    }
`


export const fetchCountriesAndLanguages = async (countryLang: string | null, host?: string, context?: any) => {
  const requestConfig: any = {
    url: `/${countryLang}/platform/countriesAndLanguages`,
    method: 'get',
    host: host
  };

  // Only add context and cookies if they exist
  if (context?.req?.cookies) {
    requestConfig.cookies = context.req.cookies;
  }
  if (context) {
    requestConfig.context = context;
  }

  const res = await request(requestConfig);
  return res.data;
}


export const fetchPreferredLanguages = async (countryLang: string | null, host?: string, cookies?: any) => {
  const res = await request({
    url: `/${countryLang}/preferredLanguages`,
    method: 'get',
    host: host,
    cookies: cookies
  });
  return res.data;
}

const IndicatorCountryLang = () => {
  const router = useRouter()
  const countryLang = useCountryLang()
  const messagesQuery = useMessages()

  const countriesAndLanguagesQuery = useQuery<ICountriesAndLanguages>(["countries-and-languages", countryLang], () =>
    fetchCountriesAndLanguages(countryLang), {
    enabled: countryLang != null,
  });

  const preferredLanguagesQuery = useQuery<IAvailableLanguage[]>(["preferred-languages", countryLang], () =>
    fetchPreferredLanguages(countryLang), {
    enabled: countryLang != null,
  });

  // Initialize state with data from server if available
  const [selectedCountry, setSelectedCountry] = useState<IAvailableCountry>(() => {
    if (countriesAndLanguagesQuery.data) {
      const country = countriesAndLanguagesQuery.data.countries?.find(
        (item: IAvailableCountry) => item.value === router.query.country
      );
      return country || {} as IAvailableCountry;
    }
    return {} as IAvailableCountry;
  });

  const [activeCountry, setActiveCountry] = useState<IAvailableCountry>(() => {
    if (countriesAndLanguagesQuery.data) {
      const country = countriesAndLanguagesQuery.data.countries?.find(
        (item: IAvailableCountry) => item.value === router.query.country
      );
      return country || {} as IAvailableCountry;
    }
    return {} as IAvailableCountry;
  });

  const [selectedLang, setSelectedLang] = useState<IAvailableLanguage>(() => {
    if (countriesAndLanguagesQuery.data) {
      const language = countriesAndLanguagesQuery.data.languages?.find(
        item => item.value === router.query.lang
      );
      return language || {} as IAvailableLanguage;
    }
    return {} as IAvailableLanguage;
  });

  const [activeLang, setActiveLang] = useState<IAvailableLanguage>(() => {
    if (countriesAndLanguagesQuery.data) {
      const language = countriesAndLanguagesQuery.data.languages?.find(
        item => item.value === router.query.lang
      );
      return language || {} as IAvailableLanguage;
    }
    return {} as IAvailableLanguage;
  });

  // Update state when data is loaded from server
  useEffect(() => {
    if (countriesAndLanguagesQuery.data) {
      const country = countriesAndLanguagesQuery.data.countries?.find(
        (item: IAvailableCountry) => item.value === router.query.country
      );
      const language = countriesAndLanguagesQuery.data.languages?.find(
        item => item.value === router.query.lang
      );

      if (country) {
        setActiveCountry(country);
        setSelectedCountry(country);
      }
      if (language) {
        setActiveLang(language);
        setSelectedLang(language);
      }
    }
  }, [countriesAndLanguagesQuery.data, router.query.country, router.query.lang]);

  const handleSaveChanges = async () => {
    router.query.country = selectedCountry.value;
    router.query.lang = selectedLang.value;
    // Persist selection to cookie (accessible in SSR)
    Cookies.set('userLang', selectedLang.value, {expires: 365}); // Set cookie to expire in 1 year
    Cookies.set('userCountry', selectedCountry.value, {expires: 365});

    // Redirect
    await router.push(router, undefined, {shallow: false})

    if (router.pathname.includes("resetcookies")) {
      window.location.reload()
    }
  }

  const content = <Contain>
    <Header>
      <span className="fs-5 fw-bold">
        {messagesQuery.data &&
          <>
            {messagesQuery.data.messages["country"]} & {messagesQuery.data.messages["language"]}
          </>
        }
      </span>
    </Header>

    {messagesQuery.data &&
      <div className="text-center">
        <div className="subheading">
          {messagesQuery.data.messages["language"]}
        </div>
      </div>
    }

    <div className="languages">

      <div className="quick-select">
        {preferredLanguagesQuery.data && selectedLang && preferredLanguagesQuery.data.map(lang => {
          return (
            <Button key={lang.value}
                    variant={"transparent"}
                    className={selectedLang.value == lang.value ? "active-lang" : ""}
                    onClick={() => {
                      if (countriesAndLanguagesQuery.data) {
                        const selected = countriesAndLanguagesQuery.data.languages.filter(entry => entry.value == lang.value)
                        setSelectedLang(selected[0])
                      }
                    }
                    }>
              {lang.label}
            </Button>
          )
        })}
      </div>

      <Select menuPlacement={"bottom"}
              styles={{
                control: (baseStyles, state): any => ({
                  ...baseStyles,
                  fontSize: "16px"
                }),
              }}
              options={countriesAndLanguagesQuery.data ? countriesAndLanguagesQuery.data.languages : []}
              value={selectedLang}
              onChange={(val: any) => setSelectedLang(val)}
      />
    </div>

    <div className="divider"/>

    {messagesQuery.data &&
      <div className="text-center">
        <div className="subheading">
          {messagesQuery.data.messages["country"]}
        </div>
      </div>
    }

    <div className="countries">
      {countriesAndLanguagesQuery.data && selectedCountry && countriesAndLanguagesQuery.data.countries?.map((country) => {
        return (
          <div key={country.label}
               className={country.value == selectedCountry.value ? "entry active" : "entry"}
               onClick={() => setSelectedCountry(country)}
          >
            <div className="flag">
              <img src={country.flag}
                   alt={country.label}/>
            </div>

            <div className="text">{country.label}</div>
          </div>
        )
      })}
    </div>

    {messagesQuery.data && selectedLang && selectedCountry &&
      <Button
        variant={selectedLang.value == activeLang.value && selectedCountry.value == activeCountry.value ? "secondary" : "primary"}
        width={"100%"}
        className={"mt-3 save-selection"}
        onClick={handleSaveChanges}
      >
        {selectedLang.saveButton}
      </Button>
    }
  </Contain>

  const text = (
    <BtnLabel>
      <Icon variant={"small"} color={"primary"}>arrow-down-s-line</Icon>
      {activeLang && activeLang.value && <span className={"label-text"}>{activeLang.value.toUpperCase()}</span>}
    </BtnLabel>
  )

  return (
    <>
      {(activeCountry && activeCountry.label) &&
        <>
          <Indicator placement={"end"} content={content} borderedImg={activeCountry.flag} imgAlt={activeCountry.label}
                     text={text}/>
        </>
      }
    </>
  )
}

export default IndicatorCountryLang